export type DefaultDriveFolder = {
  id: string;
  name: string;
};

type UserPreferences = {
  defaultDriveFolder: DefaultDriveFolder | null;
  /** Shown the "set a default folder" tip after a save without a default */
  suggestedDefaultFolder: boolean;
};

const PREFS_PREFIX = 'redact_ai_prefs_';

const DEFAULT_PREFS: UserPreferences = {
  defaultDriveFolder: null,
  suggestedDefaultFolder: false,
};

function prefsKey(userId: string | null | undefined): string {
  return `${PREFS_PREFIX}${userId || 'guest'}`;
}

function readPrefs(userId: string | null | undefined): UserPreferences {
  try {
    const raw = localStorage.getItem(prefsKey(userId));
    if (!raw) return { ...DEFAULT_PREFS };
    const parsed = JSON.parse(raw) as Partial<UserPreferences>;
    return {
      defaultDriveFolder:
        parsed.defaultDriveFolder &&
        typeof parsed.defaultDriveFolder.id === 'string' &&
        typeof parsed.defaultDriveFolder.name === 'string'
          ? parsed.defaultDriveFolder
          : null,
      suggestedDefaultFolder: Boolean(parsed.suggestedDefaultFolder),
    };
  } catch {
    return { ...DEFAULT_PREFS };
  }
}

const PREFS_CHANGED_EVENT = 'redact-ai-prefs-changed';

function writePrefs(userId: string | null | undefined, prefs: UserPreferences): void {
  localStorage.setItem(prefsKey(userId), JSON.stringify(prefs));
  window.dispatchEvent(new Event(PREFS_CHANGED_EVENT));
}

export function subscribeToPreferencesChanges(listener: () => void): () => void {
  window.addEventListener(PREFS_CHANGED_EVENT, listener);
  return () => window.removeEventListener(PREFS_CHANGED_EVENT, listener);
}

export function getDefaultDriveFolder(userId: string | null | undefined): DefaultDriveFolder | null {
  return readPrefs(userId).defaultDriveFolder;
}

export function setDefaultDriveFolder(
  userId: string | null | undefined,
  folder: DefaultDriveFolder | null
): void {
  const prefs = readPrefs(userId);
  writePrefs(userId, {
    ...prefs,
    defaultDriveFolder: folder,
    // Clear tip once they set a default
    suggestedDefaultFolder: folder ? false : prefs.suggestedDefaultFolder,
  });
}

export function shouldSuggestDefaultFolder(userId: string | null | undefined): boolean {
  const prefs = readPrefs(userId);
  return !prefs.defaultDriveFolder && prefs.suggestedDefaultFolder;
}

export function markSavedWithoutDefaultFolder(userId: string | null | undefined): void {
  const prefs = readPrefs(userId);
  if (prefs.defaultDriveFolder) return;
  writePrefs(userId, { ...prefs, suggestedDefaultFolder: true });
}

export function dismissDefaultFolderSuggestion(userId: string | null | undefined): void {
  const prefs = readPrefs(userId);
  writePrefs(userId, { ...prefs, suggestedDefaultFolder: false });
}
